/**
 * Avatar Utilities
 *
 * Pure functions for avatar initials extraction and deterministic color selection.
 * Extracted from InitialsAvatar component to centralize logic.
 *
 * @module utils/avatar
 */

/**
 * Extract initials from a full name string.
 *
 * For two-word names, returns first letter of first and last names.
 * For single-word names, returns first letter only.
 * For multi-word names (3+), returns first and last name initials.
 * Returns '?' if name is empty or whitespace only.
 *
 * @param name - Full name string (e.g., "Alice Chen")
 * @returns Uppercase initials (e.g., "AC") or "?" for invalid input
 *
 * @example
 * getInitialsFromName('Alice Chen')       // 'AC'
 * getInitialsFromName('Alice')            // 'A'
 * getInitialsFromName('Mary Jane Watson') // 'MW'
 * getInitialsFromName('')                 // '?'
 */
export function getInitialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/);

  if (parts.length === 0 || !parts[0]) {
    return '?';
  }

  if (parts.length === 1) {
    return parts[0][0]?.toUpperCase() || '?';
  }

  // Use first and last name for two or more words
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Generate initials from first and last name.
 *
 * Returns '?' if no valid characters found.
 *
 * @example
 * getInitials('Alice', 'Chen') // 'AC'
 * getInitials('Alice', null)   // 'A'
 * getInitials(null, null)      // '?'
 */
export function getInitials(
  firstName: string | null,
  lastName: string | null,
): string {
  const first = firstName?.trim()?.[0]?.toUpperCase() ?? '';
  const last = lastName?.trim()?.[0]?.toUpperCase() ?? '';
  return first + last || first || '?';
}

/**
 * Simple string hash function for deterministic value selection.
 *
 * Produces a non-negative integer hash from any string input.
 * Same input always produces same output, enabling deterministic
 * color/index selection based on user names.
 *
 * @param str - Input string to hash
 * @returns Non-negative integer hash value
 *
 * @example
 * const colorIndex = hashString('Alice Chen') % colors.length;
 */
export function hashString(str: string): number {
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0; // Convert to 32-bit integer
  }

  return Math.abs(hash);
}

const DEFAULT_INITIALS_COLORS = [
  '#E07A5F', // coral (accent)
  '#6B9E78', // green (success)
  '#8B5CF6', // purple
  '#0EA5E9', // blue
  '#F59E0B', // amber
] as const;

/**
 * Get deterministic solid color for initials avatar based on name.
 *
 * Note: The `InitialsAvatar` component uses gradients (UI concern),
 * but this utility is helpful when a solid color is needed.
 */
export function getInitialsColor(name: string): string {
  return DEFAULT_INITIALS_COLORS[hashString(name) % DEFAULT_INITIALS_COLORS.length];
}
